const STANDARD_EVENTS = new Set([
  'PageView',
  'ViewContent',
  'CompleteRegistration',
  'InitiateCheckout',
  'Purchase',
  'StartTrial',
]);

const FIRST_OPEN_KEY = (pixelId: string) => `meta.fo.${pixelId}`;

type FbqFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    fbq?: FbqFn;
    _fbq?: FbqFn;
  }
}

function pixelId(): string {
  return (import.meta.env.VITE_META_PIXEL_ID || '').trim();
}

function cookieValue(name: string): string {
  if (typeof document === 'undefined') return '';
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const [key, ...rest] = part.trim().split('=');
    if (key === name) return decodeURIComponent(rest.join('='));
  }
  return '';
}

function rememberFbclid() {
  if (typeof window === 'undefined') return;
  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  if (!fbclid || cookieValue('_fbc')) return;
  const value = `fb.1.${Date.now()}.${fbclid}`;
  document.cookie = `_fbc=${encodeURIComponent(value)}; path=/; max-age=7776000; SameSite=Lax`;
}

function loadScript() {
  if (typeof document === 'undefined' || document.getElementById('meta-fbevents')) return;
  const script = document.createElement('script');
  script.id = 'meta-fbevents';
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  try {
    document.head.appendChild(script);
  } catch {
    // jsdom/happy-dom 禁止加载外部脚本
  }
}

function ensureFbq(): FbqFn | null {
  if (typeof window === 'undefined') return null;
  if (window.fbq) return window.fbq;
  const stub: FbqFn & { callMethod?: FbqFn; queue?: unknown[]; loaded?: boolean; version?: string } = (
    ...args: unknown[]
  ) => {
    if (stub.callMethod) {
      stub.callMethod(...args);
      return;
    }
    (stub.queue ||= []).push(args);
  };
  stub.queue = [];
  stub.loaded = true;
  stub.version = '2.0';
  window.fbq = stub;
  window._fbq = stub;
  return stub;
}

export function metaClickHeaders(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  rememberFbclid();
  const headers: Record<string, string> = {
    'X-Meta-Source-Url': window.location.href,
  };
  const fbp = cookieValue('_fbp');
  const fbc = cookieValue('_fbc');
  if (fbp) headers['X-Meta-Fbp'] = fbp;
  if (fbc) headers['X-Meta-Fbc'] = fbc;
  return headers;
}

export function initMetaPixel() {
  const id = pixelId();
  if (!id) return;
  rememberFbclid();
  const fbq = ensureFbq();
  if (!fbq) return;
  loadScript();
  fbq('init', id);
  trackMeta('PageView');
  try {
    const key = FIRST_OPEN_KEY(id);
    if (!window.localStorage.getItem(key)) {
      window.localStorage.setItem(key, '1');
      trackMeta('firstOpen', { eventId: `fo_${id}` });
    }
  } catch {
    // ignore
  }
}

export function trackMetaPageView() {
  trackMeta('PageView');
}

export function trackMeta(
  eventName: string,
  options: {
    contentIds?: string[];
    contentName?: string;
    contentType?: string;
    currency?: string;
    eventId?: string;
    value?: number;
  } = {},
) {
  const id = pixelId();
  if (!id || typeof window === 'undefined') return;
  const fbq = ensureFbq();
  if (!fbq) return;
  const params: Record<string, unknown> = {};
  if (options.value != null && options.currency) {
    params.value = options.value;
    params.currency = options.currency;
  }
  if (options.contentIds?.length) params.content_ids = options.contentIds;
  if (options.contentType) params.content_type = options.contentType;
  if (options.contentName) params.content_name = options.contentName;
  const extra = options.eventId ? { eventID: options.eventId } : undefined;
  try {
    if (STANDARD_EVENTS.has(eventName)) {
      fbq('track', eventName, params, extra);
    } else {
      fbq('trackCustom', eventName, params, extra);
    }
  } catch {
    // ignore
  }
}

export function trackRegisterSubmit() {
  trackMeta('registerSubmit', { eventId: `rs_${Date.now()}` });
}

export function trackDownloadApp() {
  trackMeta('downloadApp', { eventId: `da_${Date.now()}` });
}

export function minorToMajor(amountMinor: number): number {
  return Math.round(amountMinor) / 100;
}
